import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import { FileInputContainer, FileName, StyledFileInput } from 'dialogs/DocumentDetailsDialog/components/Form/styled';
import snackbar from 'utils/snackbar';

import CustomInput, { Props as CustomInputProps } from '../../../../components/CustomInput/CustomInput';
import AssessmentSelect from '../AssessmentSelect/AssessmentSelect';
import { FlexContainer } from './styled';

interface Props {
    id?: number;
    isLoading: boolean;
    baseInputProps: Omit<CustomInputProps, 'name'>;
    companyId: number | string;
}

const grades = ['A', 'B', 'C', 'D', 'E'];

const Form = (props: Props) => {
    const { isLoading, baseInputProps, companyId } = props;
    const { data, handleAttributeChange } = baseInputProps;
    const { id, documentName } = data;

    const [fileName, setFileName] = useState(null);
    const [prevDocumentName, setPrevDocumentName] = useState('');

    const { t } = useTranslation();

    const onFileChange = useCallback((e) => {
        const file = e.target.files[0];

        if (file) {
            // show an error if the file is too big
            if (file.size / 1024 / 1024 > 32) {
                snackbar.error(t('errors.file_too_big'));
                return;
            }

            // extract file name and convert the content to base64
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (res) => {
                const base64 = res?.target?.result?.toString().split(',')[1];
                if (base64 && handleAttributeChange) {
                    setFileName(file.name);
                    handleAttributeChange('content', base64);
                    handleAttributeChange('extension', file.name.split('.').pop());

                    const newDocumentName = file.name.split('.').slice(0, -1).join('.');

                    if (documentName === prevDocumentName) {
                        handleAttributeChange('documentName', newDocumentName);
                    }

                    setPrevDocumentName(newDocumentName);
                }
            };
        }
        else if (handleAttributeChange) {
            setFileName(null);
            handleAttributeChange('content', '');
            handleAttributeChange('extension', '');
        }
    }, [documentName, handleAttributeChange, prevDocumentName, t]);

    return (
        <>
            <FlexContainer>
                <CustomInput {...baseInputProps} name='issueDate' inputProps={{ maxLength: 10 }} />

                <CustomInput {...baseInputProps} name='grade' select booleanValues>
                    {grades.map(grade => {
                        return (
                            <MenuItem value={grade} key={grade} >
                                {grade}
                            </MenuItem>
                        );
                    })}
                </CustomInput>
            </FlexContainer>

            <FlexContainer>
                <CustomInput {...baseInputProps} name='co2Saved' InputProps={{ readOnly: true }} />
                <CustomInput {...baseInputProps} name='reducedPlastic' InputProps={{ readOnly: true }} />
            </FlexContainer>

            <FlexContainer>
                <AssessmentSelect baseInputProps={baseInputProps} companyId={companyId} />
            </FlexContainer>

            {id === 0 && !isLoading && (
                <>
                    <StyledFileInput
                        id='file-input-label'
                        type='file'
                        onChange={onFileChange}
                    />

                    <FileInputContainer>
                        <label htmlFor='file-input-label'></label>
                        <Button color='primary' variant='outlined' onClick={() => document?.getElementById('file-input-label')?.click()}>
                            {t('dialogs.document_details.select_file')}
                        </Button>
                        <FileName>{fileName ?? t('dialogs.document_details.select_file_ext')}</FileName>
                    </FileInputContainer>
                </>
            )}
        </>
    );
};

export default Form;
